/**
 * Typed UI preference hooks. The server is authoritative; localStorage only avoids first-paint
 * flicker. Updates are applied optimistically and synced in the background (debounced, merged per
 * key, last write wins). Components never write arbitrary keys: only schema keys are typed.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { api, unwrap } from "@/api/client";
import type { MembershipPreferences, UserPreferences } from "@/api/types";
import { safeStorage } from "@/lib/safeStorage";

export const USER_PREF_DEFAULTS: UserPreferences = { theme: "system", reduce_motion: "system", platform_nav_mode: "pinned" };
/** Mirrors the backend defaults (core/registries/notifications.py); the server's value wins once loaded. */
export const NOTIFICATION_PREF_DEFAULTS: MembershipPreferences["notification_preferences"] = {
  approvals: { in_app: true, email: true },
  materials: { in_app: true, email: false },
  deliveries: { in_app: true, email: false },
  purchasing: { in_app: true, email: false },
  sales_orders: { in_app: true, email: false },
  work_orders: { in_app: true, email: false },
  schedule: { in_app: true, email: true },
  field: { in_app: true, email: false },
  payments: { in_app: true, email: false },
  general: { in_app: true, email: false },
};
export const MEMBERSHIP_PREF_DEFAULTS: MembershipPreferences = {
  nav_mode: "pinned",
  table_density: "comfortable",
  dashboard_layout: null,
  recent_records: [],
  notification_preferences: NOTIFICATION_PREF_DEFAULTS,
  report_preferences: {},
  agent_profile: null,
  agent_introduced: "no",
};

const userKey = ["me", "preferences"] as const;
const memberKey = (slug: string) => ["tenant", slug, "preferences"] as const;
const USER_CACHE = "kshop:prefs:user";
const memberCache = (slug: string) => `kshop:prefs:tenant:${slug}`;

type Pending = { patch: Record<string, unknown>; timer: ReturnType<typeof setTimeout> | null };
const pending = new Map<string, Pending>();

/** Whether a change of this scope ("user", "tenant:<slug>") is still waiting to be saved (live
 * updates then leave the optimistic value alone). */
export function isPreferenceSavePending(scope: string): boolean {
  return pending.has(scope);
}

function schedule(scope: string, patch: Record<string, unknown>, send: (p: Record<string, unknown>) => Promise<unknown>, onError: () => void) {
  const entry = pending.get(scope) ?? { patch: {}, timer: null };
  entry.patch = { ...entry.patch, ...patch };
  if (entry.timer) clearTimeout(entry.timer);
  entry.timer = setTimeout(() => {
    const body = entry.patch;
    pending.delete(scope);
    send(body).catch(onError);
  }, 350);
  pending.set(scope, entry);
}

export function useUserPreferences(enabled = true) {
  return useQuery({
    queryKey: userKey,
    enabled,
    queryFn: async () => {
      const prefs = await unwrap(api.GET("/api/me/preferences/"));
      safeStorage.setJSON(USER_CACHE, prefs);
      return prefs;
    },
    placeholderData: () => ({ ...USER_PREF_DEFAULTS, ...(safeStorage.getJSON<UserPreferences>(USER_CACHE) ?? {}) }),
    staleTime: 5 * 60_000,
  });
}

export function useUserPreference<K extends keyof UserPreferences>(key: K): [UserPreferences[K], (value: UserPreferences[K]) => void] {
  const qc = useQueryClient();
  const { data } = useUserPreferences();
  const value = (data ?? USER_PREF_DEFAULTS)[key];
  const setValue = useCallback(
    (next: UserPreferences[K]) => {
      // An in-flight load must not overwrite the optimistic value when it lands.
      void qc.cancelQueries({ queryKey: userKey }, { revert: false });
      const updated = { ...USER_PREF_DEFAULTS, ...(qc.getQueryData<UserPreferences>(userKey) ?? {}), [key]: next };
      qc.setQueryData(userKey, updated);
      safeStorage.setJSON(USER_CACHE, updated);
      schedule(
        "user",
        { [key]: next },
        (body) => unwrap(api.PATCH("/api/me/preferences/", { body })),
        () => qc.invalidateQueries({ queryKey: userKey }),
      );
    },
    [qc, key],
  );
  return [value, setValue];
}

export function useMembershipPreferences(slug: string, initial?: MembershipPreferences) {
  return useQuery({
    queryKey: memberKey(slug),
    queryFn: async () => {
      const prefs = await unwrap(api.GET("/api/t/{tenant_slug}/preferences/", { params: { path: { tenant_slug: slug } } }));
      safeStorage.setJSON(memberCache(slug), prefs);
      return prefs;
    },
    initialData: initial,
    placeholderData: () => ({ ...MEMBERSHIP_PREF_DEFAULTS, ...(safeStorage.getJSON<MembershipPreferences>(memberCache(slug)) ?? {}) }),
    staleTime: 5 * 60_000,
  });
}

export function useMembershipPreference<K extends keyof MembershipPreferences>(
  slug: string,
  key: K,
): [MembershipPreferences[K], (value: MembershipPreferences[K]) => void] {
  const qc = useQueryClient();
  const { data } = useMembershipPreferences(slug);
  const value = (data ?? MEMBERSHIP_PREF_DEFAULTS)[key];
  const setValue = useCallback(
    (next: MembershipPreferences[K]) => {
      void qc.cancelQueries({ queryKey: memberKey(slug) }, { revert: false });
      const updated = { ...MEMBERSHIP_PREF_DEFAULTS, ...(qc.getQueryData<MembershipPreferences>(memberKey(slug)) ?? {}), [key]: next };
      qc.setQueryData(memberKey(slug), updated);
      safeStorage.setJSON(memberCache(slug), updated);
      schedule(
        `tenant:${slug}`,
        { [key]: next },
        (body) => unwrap(api.PATCH("/api/t/{tenant_slug}/preferences/", { params: { path: { tenant_slug: slug } }, body })),
        () => qc.invalidateQueries({ queryKey: memberKey(slug) }),
      );
    },
    [qc, key, slug],
  );
  return [value, setValue];
}
